import { Funnel, FUNNEL_STEP, FUNNEL_STEPS } from '../../common/user'

export const FUNNEL_TEXT:{[step in FUNNEL_STEP]: {label:string, hint:string}} = {
    [FUNNEL_STEP.REGISTERED]: {label: "Registered", hint: "Registered themselves, with a password or a Microsoft or Google account"},
    [FUNNEL_STEP.ACTIVATED]: {label: "Activated", hint: "Clicked the activation link. Microsoft and Google accounts are activated on registering"},
    [FUNNEL_STEP.ORGANISATION]: {label: "Created organisation", hint: "Created an organisation, on registering or afterwards"},
    [FUNNEL_STEP.DESCRIBED]: {label: "AI described process", hint: "AI described a process for them to check. Users who start with a blank model skip this"},
    [FUNNEL_STEP.MODEL_BUILT]: {label: "AI built model", hint: "AI built a model for them. Users who start with a blank model skip this"},
    [FUNNEL_STEP.FIRST_EDIT]: {label: "Edited a model", hint: "Saved a change to a model themselves"},
    [FUNNEL_STEP.INSIGHTS]: {label: "Viewed insights", hint: "Opened the insights page"},
    [FUNNEL_STEP.INVITED]: {label: "Invited a colleague", hint: "Invited someone to one of their organisations"},
}

export interface FunnelRow {
    step:FUNNEL_STEP
    count:number
    ofRegistered:number             // percent of the users who registered
    ofPrevious:number|undefined     // percent of the step before. Steps can be skipped, so this can be over 100
    medianTime:number|undefined     // milliseconds from registering to reaching the step, for those who did
}

const time = (date:Date|string|undefined) => {
    const t = date ? new Date(date).getTime() : NaN
    return isNaN(t) ? undefined : t
}

const percent = (part:number, whole:number) => whole > 0 ? Math.round(100 * part / whole) : 0

const median = (values:number[]) => {
    if (values.length === 0) return undefined
    const sorted = [...values].sort((a, b) => a - b)
    const middle = Math.floor(sorted.length / 2)
    return sorted.length % 2 ? sorted[middle] : (sorted[middle - 1] + sorted[middle]) / 2
}

// how far the users who registered since a date (or ever) got through getting started. Users without a recorded
// registration (invited, or registered before the funnel was recorded) aren't counted
export const funnelSummary = (users:{funnel?:Funnel}[], since?:Date):FunnelRow[] => {
    const from = since?.getTime() ?? -Infinity
    const cohort = users
        .map(u => ({funnel:u.funnel || {}, registered:time(u.funnel?.[FUNNEL_STEP.REGISTERED])}))
        .filter(u => u.registered !== undefined && u.registered >= from)

    let previous:number|undefined
    return FUNNEL_STEPS.map(step => {
        const durations = cohort
            .map(u => {
                const reached = time(u.funnel[step])
                return reached === undefined ? undefined : Math.max(0, reached - u.registered!)
            })
            .filter((d):d is number => d !== undefined)

        const row:FunnelRow = {
            step,
            count: durations.length,
            ofRegistered: percent(durations.length, cohort.length),
            ofPrevious: previous === undefined ? undefined : percent(durations.length, previous),
            medianTime: step === FUNNEL_STEP.REGISTERED ? undefined : median(durations),
        }
        previous = durations.length
        return row
    })
}

// which users a step lists: everyone who reached it, or those who reached it and got no further
export type FunnelUserMode = 'reached' | 'stopped'

export interface FunnelUser {
    key:string
    name:string
    email:string
    registered:Date
    reached:Date
    timeToReach:number              // milliseconds from registering
    furthest:FUNNEL_STEP            // the last step of the funnel they reached
    signedIn?:Date                  // when they last signed in
}

// the users who registered since a date (or ever) and reached a step, or stopped at it, the most recent first. A user
// stops at the furthest step they reached, though they may have skipped some before it
export const usersAtStep = (users:{_key:string, name:string, email:string, funnel?:Funnel, signInDate?:Date}[], step:FUNNEL_STEP,
        since?:Date, mode:FunnelUserMode = 'reached'):FunnelUser[] => {
    const from = since?.getTime() ?? -Infinity
    return users
        .map((u):FunnelUser|undefined => {
            const registered = time(u.funnel?.[FUNNEL_STEP.REGISTERED])
            const reached = time(u.funnel?.[step])
            if (registered === undefined || registered < from || reached === undefined) return undefined
            const furthest = [...FUNNEL_STEPS].reverse().find(s => time(u.funnel?.[s]) !== undefined)!
            if (mode === 'stopped' && furthest !== step) return undefined
            const signedIn = time(u.signInDate)
            return {key: u._key, name: u.name, email: u.email, registered: new Date(registered), reached: new Date(reached),
                timeToReach: Math.max(0, reached - registered), furthest, signedIn: signedIn === undefined ? undefined : new Date(signedIn)}
        })
        .filter((u):u is FunnelUser => !!u)
        .sort((a, b) => b.reached.getTime() - a.reached.getTime())
}

// a time to reach a step, as a person would say it
export const formatDuration = (ms:number|undefined) => {
    if (ms === undefined) return ''
    const minutes = ms / 60000
    if (minutes < 1) return 'under a minute'
    if (minutes < 60) return `${Math.round(minutes)} min`
    const hours = minutes / 60
    if (hours < 48) return `${Math.round(hours)} h`
    return `${Math.round(hours / 24)} days`
}
